import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, computed, useAttrs, useSlots, useListeners } from "vue";
import type { PropType } from "vue";

import RmpInformationMenu from "@rmp/core/components/common/RmpInformationMenu.vue";
import RmpLabel from "@rmp/core/components/fields/RmpLabel.vue";
import RmpIcon from "@rmp/core/components/icon/RmpIcon.vue";
import { unmask } from "@rmp/core/utils/formatting";
import { requiredRule } from "@rmp/core/utils/validation";
import { useColors } from "@rmp/core/composables/useColors";
import { isNilOrNaN } from "@rmp/core/utils/isNilOrNaN";


export default /*#__PURE__*/_defineComponent({
  __name: 'RmpTextField',
  props: {
	value: {
		type: [String, Number] as PropType<string | number | null>,
		default: ""
	},
	placeholder: String,
	loading: Boolean,
	errors: {
		type: [Array, String] as PropType<string[] | string>,
		default: () => []
	},
	rules: {
		type: Array as PropType<unknown[]>,
		default: () => []
	},
	readonly: Boolean,
	disabled: Boolean,
	clearable: Boolean,
	hideDetails: {
		type: [Boolean, String] as PropType<boolean | "auto">,
		default: false
	},
	dense: Boolean,
	required: Boolean,
	icon: String,
	iconColor: {
		type: String,
		default: "var(--v-grey-darken1)"
	},
	appendIcon: String,
	rawValue: {
		type: Boolean,
		default: false
	},
	mask: {
		type: String,
		default: ""
	},
	type: {
		type: String,
		default: "text"
	},
	label: String,
	autocomplete: {
		type: String,
		default: "off"
	},
	name: String,
	tooltipText: String,
	suffix: String,
	hint: String,
	persistentHint: Boolean,
	// Не эмитить число, пока дробная часть не дописана (например, "12."). Нужно точечно там, где важна точность до сотых.
	deferIncompleteDecimal: {
		type: Boolean,
		default: false
	}
},
  emits: ["input", "append", "click:append"],
  setup(__props, { emit }) {

const props = __props;





const attrs = useAttrs();
const slots = useSlots();
const listeners = useListeners();

const filteredListeners = computed(() => {
	const { input, ...rest } = listeners;
	return rest;
});

const { colors } = useColors();

const inputRef = ref<{ validate: (force?: boolean) => boolean } | null>(null);

const defaultRules = computed(() => (props.required ? [requiredRule()] : []));

// Внутреннее значение как ref. Это позволяет директиве v-mask работать с ним «из коробки».
const internalValue = ref(isNilOrNaN(props.value) ? "" : String(props.value));

// Vuetify перевалидирует поле сам при смене :rules, но только если пользователь уже
// взаимодействовал с ним (hasInput/hasFocused). Если правило зависит от другого поля
// (например, максимум периода) и значение уже заполнено, ошибка должна появиться сразу,
// а не только после того как пользователь тронет это же поле руками.
watch(
	() => [...defaultRules.value, ...props.rules],
	() => {
		if(internalValue.value !== "") inputRef.value?.validate(true);
	},
	{ deep: true }
);

// Если значение во входном prop изменилось извне – обновляем internalValue.
watch(
	() => props.value,
	(newVal) => {
		const newStr = isNilOrNaN(props.value) ? "" : String(newVal);
		if(newStr !== internalValue.value) {
			if(props.mask && unmask(String(internalValue.value), props.mask) === newStr) return;
			internalValue.value = newStr;
		}
	}
);

const resolvedType = computed(() => {
	return props.type === "number" ? "text" : props.type;
});

// При изменении internalValue обрабатываем значение и эмитим его в родительский компонент.
watch(
	internalValue,
	(newVal) => {
		let processed: string | number = newVal ?? "";
		if(props.type === "number" && processed.includes(",")) {
			processed = processed.replace(",", ".");
		}
		if(!props.rawValue && props.mask) {
			processed = unmask(processed, props.mask);
		}
		if(props.type === "number" && processed !== "") {
			// Строка вида "12." ещё не дописана: Number("12.") === 12, но эмитить как готовое число нельзя,
			// иначе теряется факт незавершённого ввода дробной части.
			if(props.deferIncompleteDecimal && /[.,]$/.test(String(processed))) return;

			const parsed = Number(processed);
			if(isNaN(parsed)) return;
			processed = parsed;
		}
		if(String(processed) !== String(props.value)) {
			emit("input", processed);
		}
	}
);

return { __sfc: true,props, emit, attrs, slots, listeners, filteredListeners, colors, inputRef, defaultRules, internalValue, resolvedType, RmpInformationMenu, RmpLabel, RmpIcon }
}

})